import { useEffect, useState } from "react";
import { Gift, Lock, Clock, CheckCircle2, BookOpen, Utensils, Plane, Smartphone, Bike, Car, Home, Gem } from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CLUB_STYLE = {
  "Bronze Club": {
    bg: "linear-gradient(135deg, #b45309 0%, #d97706 100%)",
    ring: "border-amber-300", chip: "bg-amber-100 text-amber-800",
    Icon: BookOpen, bar: "bg-amber-500",
  },
  "Silver Club": {
    bg: "linear-gradient(135deg, #52525b 0%, #71717a 100%)",
    ring: "border-slate-300", chip: "bg-slate-100 text-slate-800",
    Icon: Utensils, bar: "bg-slate-500",
  },
  "Gold Club": {
    bg: "linear-gradient(135deg, #b45309 0%, #ca8a04 100%)",
    ring: "border-yellow-300", chip: "bg-yellow-100 text-yellow-800",
    Icon: Plane, bar: "bg-yellow-500",
  },
  "Pearl Club": {
    bg: "linear-gradient(135deg, #115e59 0%, #0f766e 100%)",
    ring: "border-teal-300", chip: "bg-teal-100 text-teal-800",
    Icon: Smartphone, bar: "bg-teal-500",
  },
  "Coral Club": {
    bg: "linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)",
    ring: "border-red-300", chip: "bg-red-100 text-red-800",
    Icon: Bike, bar: "bg-red-500",
  },
  "Topaz Club": {
    bg: "linear-gradient(135deg, #075985 0%, #0369a1 100%)",
    ring: "border-sky-300", chip: "bg-sky-100 text-sky-800",
    Icon: Car, bar: "bg-sky-500",
  },
  "Ruby Club": {
    bg: "linear-gradient(135deg, #9f1239 0%, #e11d48 100%)",
    ring: "border-rose-300", chip: "bg-rose-100 text-rose-800",
    Icon: Home, bar: "bg-rose-500",
  },
  "Diamond Club": {
    bg: "linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)",
    ring: "border-indigo-300", chip: "bg-indigo-100 text-indigo-800",
    Icon: Gem, bar: "bg-indigo-500",
  },
};

const STATE = {
  locked:      { label: "Locked",      cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Lock },
  in_progress: { label: "In Progress", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: Clock },
  complete:    { label: "Complete",    cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
};

const ordinal = (n) => {
  if (n === 1) return "1st";
  if (n === 2) return "2nd";
  if (n === 3) return "3rd";
  return `${n}th`;
};

export default function RewardClubs() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reward-clubs");
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-5 text-slate-900">
        {/* Hero card */}
        <section className="rounded-2xl overflow-hidden shadow-xl">
          <div className="p-5 text-white"
               style={{ background: "linear-gradient(90deg, #7c2d12 0%, #991b1b 100%)" }}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                <Gift size={20} />
              </div>
              <div>
                <div className="font-black text-xl" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Rewards Program
                </div>
                <div className="text-xs opacity-90">Achieve downline targets — earn amazing rewards</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-5 grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Total Team</div>
              <div data-testid="rc-total-team"
                   className="text-2xl font-black mt-1"
                   style={{ fontFamily: "Outfit, sans-serif" }}>
                {data?.total_team ?? "—"}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Current Club</div>
              <div data-testid="rc-current-club"
                   className="text-lg font-extrabold text-red-700 mt-1"
                   style={{ fontFamily: "Outfit, sans-serif" }}>
                {data?.current_club || "None yet"}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Next Target</div>
              <div data-testid="rc-next-target"
                   className="text-2xl font-black text-emerald-600 mt-1"
                   style={{ fontFamily: "Outfit, sans-serif" }}>
                {data?.next_target || "—"}
              </div>
            </div>
          </div>
        </section>

        {loading || !data ? (
          <div className="py-16 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-white/40 border-t-white animate-spin" />
          </div>
        ) : (
          <div data-testid="reward-clubs-grid" className="space-y-4">
            {data.clubs.map((c) => {
              const style = CLUB_STYLE[c.club] || CLUB_STYLE["Bronze Club"];
              const s = STATE[c.state] || STATE.locked;
              const Icon = style.Icon;
              return (
                <section
                  key={c.club}
                  data-testid={`club-${c.level}`}
                  className={`rounded-2xl overflow-hidden shadow-lg border-2 bg-white ${style.ring}`}
                >
                  <div className="p-4 flex items-center justify-between text-white"
                       style={{ background: style.bg }}>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-white/25 flex items-center justify-center">
                        <Icon size={18} />
                      </div>
                      <div>
                        <div className="font-extrabold text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>
                          {c.club}
                        </div>
                        <div className="text-[11px] uppercase tracking-widest opacity-80">Level - {c.level}</div>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full border ${s.cls}`}>
                      <s.Icon size={12} /> {s.label}
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${style.chip}`}>
                        {ordinal(c.depth)} Downline
                      </span>
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${style.chip}`}>
                        {c.need} {c.rank}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Reward</div>
                        <div className="font-extrabold text-slate-900 text-base sm:text-lg mt-0.5"
                             style={{ fontFamily: "Outfit, sans-serif" }}>
                          {c.reward.toUpperCase()}
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{c.desc}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                          {ordinal(c.depth)} Downline
                        </div>
                        <div className="text-xl font-extrabold text-slate-900 tabular-nums"
                             style={{ fontFamily: "Outfit, sans-serif" }}>
                          {c.have} <span className="text-slate-400 text-sm">/ {c.need}</span>
                        </div>
                        {!c.achieved && (
                          <div className="text-[11px] text-slate-500">
                            {c.need - c.have} more needed
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                        <div className={`h-full ${style.bar} transition-all`}
                             style={{ width: `${c.percent}%` }} />
                      </div>
                      <div className="text-right text-[11px] text-slate-500 mt-1">
                        {c.percent}% complete
                      </div>
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}

        <section className="rounded-2xl bg-blue-50 border border-blue-200 p-4 text-xs text-blue-900 leading-relaxed">
          <b>How Rewards Work:</b> Each club level requires a specific number of members with the required
          rank in that exact downline level. For example, Bronze Club needs 5 Executives in your 1st downline;
          Silver Club needs 13 Promoters in your 2nd downline. Once the target is met, the reward is
          unlocked and provided by the company.
        </section>
      </main>
    </div>
  );
}
